import React, { useEffect, useRef, useState } from 'react'
import Select from 'react-select'
import RangeSlider from './RangeSlider'

const RETRIES_OPTIONS = [
    { value: '', label: 'Any' },
    { value: 'false', label: 'Open' },
    { value: 'true', label: 'Exhausted' },
]

const WEEKDAYS = [
    { value: 0, label: 'Mon' },
    { value: 1, label: 'Tue' },
    { value: 2, label: 'Wed' },
    { value: 3, label: 'Thu' },
    { value: 4, label: 'Fri' },
    { value: 5, label: 'Sat' },
    { value: 6, label: 'Sun' },
]

const DRIVER_STAGES = [
    { value: -3, label: 'Unknown' },
    { value: -1, label: 'Denied' },
    { value: 0, label: "Didn't take a car" },
    { value: 1, label: 'Applied' },
    { value: 3, label: 'Insurance Approved' },
    { value: 5, label: 'Awaiting Approval' },
    { value: 6, label: 'Active' },
    { value: 10, label: 'Has Rented' },
]

const OPENED_PRESETS = [
    { value: 1, label: 'Today' },
    { value: 7, label: 'Last 7d' },
    { value: 30, label: 'Last 30d' },
    { value: 90, label: 'Last 90d' },
]

function Section({ title, children }) {
    return (
        <div className="cd-filtersection">
            <div className="cd-filtersection-title">{title}</div>
            {children}
        </div>
    )
}

function Segmented({ options, value, onChange, ariaLabel }) {
    return (
        <div className="cd-segmented" role="radiogroup" aria-label={ariaLabel}>
            {options.map((o) => (
                <button
                    key={o.value || 'any'}
                    type="button"
                    role="radio"
                    aria-checked={value === o.value}
                    className={value === o.value ? 'active' : ''}
                    onClick={() => onChange(o.value)}
                >
                    {o.label}
                </button>
            ))}
        </div>
    )
}

function WeekdayPicker({ value, onChange }) {
    return (
        <div className="cd-weekday-grid">
            {WEEKDAYS.map((d) => (
                <button
                    key={d.value}
                    type="button"
                    role="radio"
                    aria-checked={value === d.value}
                    className={value === d.value ? 'active' : ''}
                    onClick={() => onChange(value === d.value ? '' : d.value)}
                >
                    {d.label}
                </button>
            ))}
            {value !== '' && value != null && (
                <button type="button" className="clear" onClick={() => onChange('')}>
                    Clear day
                </button>
            )}
        </div>
    )
}

export default function FilterPopover({ open, anchorRef, value, onApply, onClose }) {
    const popoverRef = useRef(null)
    const [draft, setDraft] = useState(value)

    useEffect(() => {
        if (open) setDraft(value)
    }, [open, value])

    useEffect(() => {
        if (!open) return
        function onDocClick(e) {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(e.target) &&
                anchorRef.current &&
                !anchorRef.current.contains(e.target)
            ) {
                onClose()
            }
        }
        function onKey(e) {
            if (e.key === 'Escape') onClose()
        }
        document.addEventListener('mousedown', onDocClick)
        document.addEventListener('keydown', onKey)
        return () => {
            document.removeEventListener('mousedown', onDocClick)
            document.removeEventListener('keydown', onKey)
        }
    }, [open, onClose, anchorRef])

    if (!open) return null

    const update = (key, v) => setDraft((d) => ({ ...d, [key]: v }))

    const apply = () => {
        const clean = { ...draft }
        Object.keys(clean).forEach((k) => {
            if (clean[k] === '' || clean[k] == null) delete clean[k]
        })
        onApply(clean)
    }
    const reset = () => {
        setDraft({})
        onApply({})
    }

    const activeCount = Object.values(draft).filter((v) => v !== '' && v != null).length

    return (
        <div ref={popoverRef} className="cd-filterpopover" role="dialog" aria-label="Filters">
            <header className="cd-filter-header">
                <span className="cd-filter-title">Filters</span>
                {activeCount > 0 && (
                    <span className="cd-filter-activecount">{activeCount} active</span>
                )}
                <button
                    type="button"
                    className="cd-filter-close"
                    onClick={onClose}
                    aria-label="Close filters"
                >
                    ×
                </button>
            </header>

            <div className="cd-filter-body">
                <Section title="Driver stage">
                    <Select
                        isMulti
                        isClearable
                        placeholder="All stages"
                        options={DRIVER_STAGES}
                        value={DRIVER_STAGES.filter((opt) =>
                            Array.isArray(draft.driverStages)
                            && draft.driverStages.includes(opt.value),
                        )}
                        onChange={(selected) => {
                            const next = (selected || []).map((o) => o.value)
                            update('driverStages', next.length > 0 ? next : '')
                        }}
                        classNamePrefix="cd-select"
                        styles={{
                            control: (base, state) => ({
                                ...base,
                                minHeight: 36,
                                borderRadius: 8,
                                borderColor: state.isFocused ? '#0f5132' : '#d6dae1',
                                boxShadow: state.isFocused
                                    ? '0 0 0 3px rgba(15, 81, 50, 0.08)'
                                    : 'none',
                                '&:hover': { borderColor: '#0f5132' },
                                fontSize: 13,
                            }),
                            multiValue: (base) => ({
                                ...base,
                                background: '#d1fae5',
                                borderRadius: 6,
                            }),
                            multiValueLabel: (base) => ({
                                ...base,
                                color: '#0f5132',
                                fontWeight: 500,
                                fontSize: 11,
                            }),
                            multiValueRemove: (base) => ({
                                ...base,
                                color: '#0f5132',
                                ':hover': { background: '#a7f3d0', color: '#064e3b' },
                            }),
                            placeholder: (base) => ({ ...base, fontSize: 13, color: '#9ca3af' }),
                            menu: (base) => ({ ...base, zIndex: 50 }),
                            option: (base, state) => ({
                                ...base,
                                fontSize: 13,
                                background: state.isSelected
                                    ? '#0f5132'
                                    : state.isFocused
                                    ? '#f1f3f7'
                                    : 'transparent',
                                color: state.isSelected ? '#fff' : '#1f2937',
                            }),
                        }}
                    />
                </Section>

                <Section title="Billing day">
                    <WeekdayPicker
                        value={draft.driverBillingDay ?? ''}
                        onChange={(v) => update('driverBillingDay', v)}
                    />
                </Section>

                <Section title="Opening balance">
                    <RangeSlider
                        min={0}
                        max={5000}
                        step={25}
                        low={draft.openingBalanceGte}
                        high={draft.openingBalanceLte}
                        onChange={({ low, high }) => {
                            update('openingBalanceGte', low)
                            update('openingBalanceLte', high)
                        }}
                        formatValue={(v) => `$${Number(v).toLocaleString()}`}
                    />
                </Section>

                <Section title="Opened">
                    <div className="cd-opened-presets" role="radiogroup" aria-label="Opened relative range">
                        {OPENED_PRESETS.map((p) => {
                            const active = Number(draft.openedWithinDays) === p.value
                            return (
                                <button
                                    key={p.value}
                                    type="button"
                                    role="radio"
                                    aria-checked={active}
                                    className={active ? 'active' : ''}
                                    onClick={() => {
                                        if (active) {
                                            update('openedWithinDays', '')
                                        } else {
                                            update('openedWithinDays', p.value)
                                            // Relative range supersedes the absolute fields.
                                            update('openedAtDateGte', '')
                                            update('openedAtDateLte', '')
                                        }
                                    }}
                                >
                                    {p.label}
                                </button>
                            )
                        })}
                    </div>
                    <div className="cd-opened-or">or pick a custom range</div>
                    <div className="cd-filter-daterow">
                        <input
                            type="date"
                            value={draft.openedAtDateGte ?? ''}
                            onChange={(e) => {
                                update('openedAtDateGte', e.target.value)
                                if (e.target.value) update('openedWithinDays', '')
                            }}
                            aria-label="Opened on or after"
                        />
                        <span className="cd-range-dash">→</span>
                        <input
                            type="date"
                            value={draft.openedAtDateLte ?? ''}
                            onChange={(e) => {
                                update('openedAtDateLte', e.target.value)
                                if (e.target.value) update('openedWithinDays', '')
                            }}
                            aria-label="Opened on or before"
                        />
                    </div>
                </Section>

                <Section title="Attempts">
                    <Segmented
                        ariaLabel="Attempts"
                        value={draft.retriesExhausted ?? ''}
                        onChange={(v) => update('retriesExhausted', v)}
                        options={RETRIES_OPTIONS}
                    />
                </Section>

                <Section title="Assigned collector">
                    <input
                        className="cd-filter-input"
                        type="number"
                        placeholder="Collector user ID"
                        value={draft.assignedToId ?? ''}
                        onChange={(e) =>
                            update(
                                'assignedToId',
                                e.target.value ? Number(e.target.value) : '',
                            )
                        }
                    />
                </Section>
            </div>

            <footer className="cd-filter-footer">
                <button type="button" className="cd-btn outline" onClick={reset}>
                    Reset
                </button>
                <button type="button" className="cd-btn primary" onClick={apply}>
                    Apply filters
                </button>
            </footer>
        </div>
    )
}
